// src/components/admin/AdminSidebar.tsx - Clean & Refined Design
import React, { useMemo, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
  Badge,
  useTheme,
  alpha,
  Avatar,
  LinearProgress,
  Chip
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Business as BusinessIcon,
  Settings as SettingsIcon,
  Assessment as AnalyticsIcon,
  Description as ReportsIcon,
  Notifications as NotificationsIcon,
  Security as SecurityIcon,
  AdminPanelSettings as AdminIcon,
  TrendingUp as TrendingUpIcon,
  Speed as SpeedIcon,
  CloudDone as CloudIcon,
  Article as ArticleIcon,
  Storefront as StorefrontIcon,
  RateReview as ReviewIcon,
  SvgIconComponent,
  SupportAgent as SupportIcon,
  ViewCarousel as ViewCarouselIcon
} from '@mui/icons-material';
import { usePermissions, Permission } from '../../hooks/usePermissions';
import * as adminApi from '../../services/admin';
import * as notificationApi from '../../services/notification';

interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  userType: string;
}

interface AdminSidebarProps {
  open: boolean;
  onClose: () => void;
  isAuthenticated: boolean;
  user: User | null;
}

interface AdminMenuItem {
  id: string;
  label: string;
  path: string;
  icon: React.ReactElement;
  badge?: number;
  color?: string;
  permission?: Permission;
}

type ChipColor = 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';

interface StatItem {
  icon: SvgIconComponent;
  label: string;
  value: string;
  color: ChipColor;
}

const AdminSidebar: React.FC<AdminSidebarProps> = ({ 
  open, 
  onClose, 
  isAuthenticated, 
  user 
}) => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const isDarkMode = theme.palette.mode === 'dark';
  const { hasPermission, isSuperAdmin } = usePermissions();

  const [stats, setStats] = useState({
    totalUsers: 0,
    totalExperts: 0,
    pendingUsers: 0
  });

  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setIsRefreshing(true);
        const data = await adminApi.getStats();
        setStats({
          totalUsers: data.totalUsers,
          totalExperts: data.totalExperts,
          pendingUsers: data.pendingUsers
        });
      } catch (error) {
        console.error('Failed to fetch stats:', error);
      } finally {
        setIsRefreshing(false);
      }
    };

    const fetchNotifications = async () => {
      try {
        const count = await notificationApi.getUnreadCount();
        setUnreadNotifications(count);
      } catch (error) {
        console.error('Failed to fetch notification count:', error);
      }
    };

    const fetchAllData = async () => {
      await Promise.all([fetchStats(), fetchNotifications()]);
    };

    fetchAllData();
    // Refresh every 10 seconds for real-time updates
    const interval = setInterval(fetchAllData, 10000);
    return () => clearInterval(interval);
  }, []);

  const adminMenuItems: AdminMenuItem[] = useMemo(() => [
    {
      id: 'dashboard',
      label: 'Dashboard',
      path: '/admin/dashboard',
      icon: <DashboardIcon />,
      color: theme.palette.primary.main,
      permission: 'dashboard'
    },
    {
      id: 'analytics',
      label: 'Analytics',
      path: '/admin/analytics',
      icon: <AnalyticsIcon />,
      color: theme.palette.info.main,
      permission: 'dashboard'
    },
    {
      id: 'reports',
      label: 'Reports',
      path: '/admin/reports',
      icon: <ReportsIcon />,
      color: '#00bcd4',
      permission: 'dashboard'
    },
    {
      id: 'users',
      label: 'Users',
      path: '/admin/users',
      icon: <PeopleIcon />,
      badge: stats.pendingUsers,
      color: theme.palette.secondary.main,
      permission: 'user_management'
    },
    {
      id: 'industries',
      label: 'Industries',
      path: '/admin/industries',
      icon: <BusinessIcon />,
      color: theme.palette.success.main,
      permission: 'industries'
    },
    {
      id: 'marketplace',
      label: 'Marketplace',
      path: '/admin/marketplace',
      icon: <StorefrontIcon />,
      color: '#9c27b0',
      permission: 'marketplace'
    },
    {
      id: 'content',
      label: 'Content',
      path: '/admin/content',
      icon: <ArticleIcon />,
      color: theme.palette.warning.main,
      permission: 'content_management'
    },
    {
      id: 'testimonials',
      label: 'Testimonials',
      path: '/admin/testimonials',
      icon: <ReviewIcon />,
      color: '#e91e63',
      permission: 'content_management'
    },
    {
      id: 'hero',
      label: 'Hero Section',
      path: '/admin/hero-section',
      icon: <ViewCarouselIcon />,
      color: '#673ab7',
      permission: 'home_content'
    },
    {
      id: 'support',
      label: 'Support',
      path: '/admin/support',
      icon: <SupportIcon />,
      color: '#00bcd4',
      permission: 'support_messages'
    },
    {
      id: 'admin-management',
      label: 'Admin Management',
      path: '/admin/admin-management',
      icon: <AdminIcon />,
      color: '#ff5722',
      permission: 'admin_management'
    },
    {
      id: 'notifications',
      label: 'Notifications',
      path: '/admin/notifications',
      icon: <NotificationsIcon />,
      badge: unreadNotifications,
      color: theme.palette.error.main,
      permission: 'dashboard'
    },
    {
      id: 'security',
      label: 'Security',
      path: '/admin/security',
      icon: <SecurityIcon />,
      color: '#f57c00',
      permission: 'admin_management'
    },
    {
      id: 'settings',
      label: 'Settings',
      path: '/admin/settings',
      icon: <SettingsIcon />,
      color: '#795548',
      permission: 'admin_management'
    }
  ], [stats.pendingUsers, unreadNotifications, theme.palette]);

  // Filter menu items based on permissions
  const filteredMenuItems = useMemo(() => {
    // Super admins see everything
    if (isSuperAdmin) {
      return adminMenuItems;
    }

    // Sub-admins only see items they have permission for
    return adminMenuItems.filter(item => {
      if (!item.permission) {
        return true; // Items without permission requirement are visible to all admins
      }
      return hasPermission(item.permission);
    });
  }, [adminMenuItems, hasPermission, isSuperAdmin]);

  const handleNavigation = (path: string): void => {
    navigate(path);
    onClose();
  };

  const isActiveRoute = (path: string): boolean => {
    return location.pathname === path || 
           (path === '/admin/dashboard' && location.pathname === '/admin');
  };

  const getUserInitials = (): string => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`;
    }
    return 'AD';
  };

  const quickStats: StatItem[] = useMemo(() => [
    {
      icon: PeopleIcon,
      label: 'Total Users',
      value: stats.totalUsers.toLocaleString(),
      color: 'primary'
    },
    {
      icon: TrendingUpIcon,
      label: 'Experts',
      value: stats.totalExperts.toLocaleString(),
      color: 'success'
    },
    {
      icon: SpeedIcon,
      label: 'Pending',
      value: stats.pendingUsers.toLocaleString(),
      color: 'warning'
    }
  ], [stats]);

  const drawerWidth = 280;

  return (
    <Drawer
      variant="temporary"
      anchor="left"
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          background: isDarkMode
            ? 'linear-gradient(180deg, rgba(18, 18, 18, 0.95) 0%, rgba(25, 25, 25, 0.95) 100%)'
            : 'linear-gradient(180deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
          backdropFilter: 'blur(20px)',
          borderRight: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          boxShadow: '4px 0 20px rgba(0,0,0,0.1)',
          mt: { xs: 7, sm: 8 }
        },
      }}
    >
      <Box sx={{ overflow: 'auto', height: '100%', display: 'flex', flexDirection: 'column' }}>
        
        {/* Admin Header */}
        <Box 
          sx={{
            p: 3,
            background: isDarkMode
              ? alpha('#000', 0.3)
              : alpha('#f5f5f5', 0.5),
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            borderRadius: '0 0 16px 16px',
            m: 1,
            mb: 2
          }}
        >
          <Chip
            icon={<AdminIcon />}
            label="Admin Panel"
            sx={{
              background: isDarkMode 
                ? 'linear-gradient(135deg, #d9f316 0%, #b8d113 100%)'
                : 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
              color: isDarkMode ? '#000' : '#fff',
              fontWeight: 600,
              mb: 2
            }}
          />
          
          {user && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar
                sx={{
                  width: 40,
                  height: 40,
                  background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                  color: '#fff',
                  fontWeight: 600,
                }}
              >
                {getUserInitials()}
              </Avatar>
              <Box>
                <Typography variant="subtitle2" fontWeight={600}>
                  {user.firstName} {user.lastName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Administrator
                </Typography>
              </Box>
            </Box>
          )}
        </Box>

        {/* Quick Stats */}
        <Box
          sx={{
            p: 2,
            m: 1,
            background: alpha(theme.palette.background.paper, 0.5),
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            mb: 2,
            position: 'relative',
            transition: 'all 0.3s ease'
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 600, textTransform: 'uppercase' }}
            >
              Overview
            </Typography>
            {isRefreshing && (
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  bgcolor: 'success.main',
                  animation: 'pulse 1.5s ease-in-out infinite',
                  '@keyframes pulse': {
                    '0%, 100%': { opacity: 1 },
                    '50%': { opacity: 0.3 }
                  }
                }}
              />
            )}
          </Box>
          
          {quickStats.map((stat, index) => {
            const IconComponent = stat.icon;
            
            return (
              <Box 
                key={index}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  p: 1,
                  borderRadius: 1,
                  mb: index === quickStats.length - 1 ? 0 : 1
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <IconComponent 
                    color={stat.color === 'default' ? 'inherit' : stat.color} 
                    fontSize="small" 
                  />
                  <Typography variant="body2">{stat.label}</Typography>
                </Box>
                <Typography variant="body2" fontWeight={600} color={`${stat.color}.main`}>
                  {stat.value}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {/* Navigation Menu */}
        <List sx={{ flex: 1, px: 1 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ px: 2, py: 1, display: 'block', fontWeight: 600, textTransform: 'uppercase' }}
          >
            Navigation
          </Typography>

          {filteredMenuItems.map((item) => (
            <ListItem key={item.id} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                onClick={() => handleNavigation(item.path)}
                selected={isActiveRoute(item.path)}
                sx={{
                  borderRadius: 2,
                  mx: 1,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    background: alpha(item.color || theme.palette.primary.main, 0.08),
                  },
                  '&.Mui-selected': {
                    background: alpha(item.color || theme.palette.primary.main, 0.12),
                    borderLeft: `3px solid ${item.color || theme.palette.primary.main}`,
                    '& .MuiListItemIcon-root': {
                      color: item.color || theme.palette.primary.main,
                    },
                    '& .MuiListItemText-primary': {
                      fontWeight: 600,
                      color: item.color || theme.palette.primary.main,
                    }
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  {item.badge ? (
                    <Badge 
                      badgeContent={item.badge} 
                      color="error"
                      sx={{
                        '& .MuiBadge-badge': {
                          fontSize: '0.65rem',
                          minWidth: 16,
                          height: 16
                        }
                      }}
                    >
                      {item.icon}
                    </Badge>
                  ) : (
                    item.icon
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontSize: '0.9rem'
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>

        {/* System Status */}
        <Box 
          sx={{
            m: 1,
            p: 2,
            background: alpha(theme.palette.success.main, 0.08),
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`
          }}
        >
          <Typography 
            variant="caption" 
            color="success.main" 
            sx={{ mb: 1, display: 'block', fontWeight: 600, textTransform: 'uppercase' }}
          >
            System Status
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
            <Box 
              sx={{ 
                width: 8, 
                height: 8, 
                borderRadius: '50%', 
                bgcolor: 'success.main'
              }} 
            />
            <Typography variant="body2">All systems operational</Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
            <CloudIcon sx={{ fontSize: 16, color: 'success.main' }} />
            <Typography variant="caption" color="text.secondary">
              Last backup: 2 hours ago
            </Typography>
          </Box>
          
          <Box sx={{ mb: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary">Performance</Typography>
              <Typography variant="caption" color="success.main" fontWeight={600}>98%</Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={98} 
              sx={{ 
                height: 4, 
                borderRadius: 2,
                bgcolor: alpha(theme.palette.success.main, 0.2),
                '& .MuiLinearProgress-bar': {
                  bgcolor: 'success.main',
                  borderRadius: 2
                }
              }}
            />
          </Box>
        </Box>

        {/* Version Info */}
        <Box 
          sx={{
            mt: 'auto',
            p: 2,
            textAlign: 'center',
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            background: alpha(theme.palette.background.paper, 0.3)
          }}
        >

          <Typography variant="caption" color="text.secondary">
            Admin Panel v2.1.0
          </Typography>
          <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center', gap: 0.5 }}>
            <Chip 
              label="Secure" 
              size="small" 
              color="success" 
              sx={{ fontSize: '0.6rem', height: 18 }}
            />
            <Chip 
              label="Updated" 
              size="small" 
              color="info" 
              sx={{ fontSize: '0.6rem', height: 18 }}
            />
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
};

export default AdminSidebar;